package com.devi3east.bashbuddy.account.organization.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.consumeWindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.devi3east.bashbuddy.account.components.AccountCard
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.auth.ui.AuthUiState
import com.devi3east.bashbuddy.common.domain.models.Address
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.ConfirmDeleteDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.common.util.PhoneNumberTransformation
import kotlinx.coroutines.launch

@Composable
fun OrganizationRoute(
    appState: AppState,
    profile: Profile
) {
    val uiState by appState.authViewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        appState.authViewModel.loadOrganizationData(profile._id)
    }

    LaunchedEffect(uiState.organization) {
        if (uiState.organization == null) {
            appState.stripeViewModel.clearStripeState()
        } else {
            appState.stripeViewModel.loadStripeAccount()
        }
    }

    OrganizationScreen(
        appState = appState,
        profile = profile,
        uiState = uiState,
        onSignOut = {
            appState.authViewModel.signOut()
        },
        onUpdateOrganization = { updatedOrg ->
            appState.authViewModel.updateOrganizationInfo(updatedOrg)
        },
        onStripeSetupClick = { email ->
            appState.stripeViewModel.stripeOnboarding(email)
        },
        onDashboardLinkClick = { url ->
            appState.stripeViewModel.openUrl(url)
        },
        onResumeStripeSetup = { accountId ->
            appState.stripeViewModel.resumeStripeOnboarding(accountId)
        },
        onToggleAccount = { appState.authViewModel.toggleAccountActivation() },
        onConfirmDeleteAccount = { appState.authViewModel.deleteAccount() }
    )
}

@Composable
fun OrganizationScreen(
    appState: AppState,
    profile: Profile,
    uiState: AuthUiState,
    onSignOut: () -> Unit,
    onUpdateOrganization: suspend (Organization) -> Unit,
    onStripeSetupClick: suspend (String) -> Unit,
    onDashboardLinkClick: (String) -> Unit,
    onResumeStripeSetup: (String) -> Unit,
    onToggleAccount: () -> Unit,
    onConfirmDeleteAccount: () -> Unit
) {

    val cardShape = RoundedCornerShape(16.dp)
    val infoInteraction = remember { MutableInteractionSource() }
    val stripeInteraction = remember { MutableInteractionSource() }
    val settingsInteraction = remember { MutableInteractionSource() }
    var isEditing by remember { mutableStateOf(false) }
    var expandInfo by remember { mutableStateOf(false) }
    var expandStripe by remember { mutableStateOf(false) }
    var expandSettings by remember { mutableStateOf(false) }
    val coroutineScope = rememberCoroutineScope()
    var isConnectingToStripe by remember { mutableStateOf(false) }

    val isDeactivated = !uiState.isActive
    var showDeleteDialog by remember { mutableStateOf(false) }

    val currentOrganization = uiState.organization
    if (currentOrganization == null) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
        }
        return
    }

    // Fields
    var organizationName by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf(profile.displayName ?: "") }
    var displayNameError by remember { mutableStateOf(false) }
    var organizationPhone by remember { mutableStateOf("") }
    var contactPerson by remember { mutableStateOf("") }
    var addressLine1 by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var state by remember { mutableStateOf("") }
    var zipCode by remember { mutableStateOf("") }

    LaunchedEffect(currentOrganization) {
        displayName = profile.displayName ?: ""
        organizationName = currentOrganization.organizationName.orEmpty()
        organizationPhone = currentOrganization.phone.orEmpty()
        contactPerson = currentOrganization.contactPerson.orEmpty()
        addressLine1 = currentOrganization.address?.line1.orEmpty()
        city = currentOrganization.address?.city.orEmpty()
        state = currentOrganization.address?.state.orEmpty()
        zipCode = currentOrganization.address?.zipCode.orEmpty()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
    ) {
        Scaffold { innerPadding ->
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxSize()
                    .consumeWindowInsets(innerPadding)
            ) {
                val maxWidthDp = maxWidth
                val targetWidth = when {
                    maxWidthDp < 600.dp -> maxWidthDp
                    maxWidthDp < 900.dp -> 600.dp
                    else -> 720.dp
                }

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .imePadding()
                        .padding(
                            start = 16.dp,
                            end = 16.dp,
                            top = innerPadding.calculateTopPadding() + 8.dp,
                            bottom = innerPadding.calculateBottomPadding() + 16.dp
                        )
                ) {
                    Box(
                        modifier = Modifier
                            .widthIn(max = targetWidth)
                    ) {

                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier.fillMaxWidth()
                        ) {

                            Text(
                                text = profile.displayName ?: "Organization",
                                style = MaterialTheme.typography.headlineMedium,
                                modifier = Modifier.padding(vertical = 16.dp),
                                color = MaterialTheme.colorScheme.onBackground
                            )

                            AccountCard(
                                title = "Organization Information",
                                expanded = expandInfo,
                                onToggle = { expandInfo = !expandInfo },
                                interactionSource = infoInteraction,
                                shape = cardShape
                            ) {
                                OutlinedTextField(
                                    value = displayName,
                                    onValueChange = { displayName = it.take(20) },
                                    label = { Text("Display Name") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                if (displayNameError) {
                                    Text(
                                        modifier = Modifier.padding(top = 4.dp, start = 4.dp),
                                        text = "Display Name is required.",
                                        color = MaterialTheme.colorScheme.error,
                                        style = MaterialTheme.typography.bodySmall
                                    )
                                }

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = organizationName,
                                    onValueChange = { organizationName = it },
                                    label = { Text("Organization Name") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = organizationPhone,
                                    onValueChange = {
                                        organizationPhone = it.filter { c -> c.isDigit() }.take(10)
                                    },
                                    label = { Text("Phone") },
                                    visualTransformation = PhoneNumberTransformation(),
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = contactPerson,
                                    onValueChange = { contactPerson = it },
                                    label = { Text("Contact Person") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = addressLine1,
                                    onValueChange = { addressLine1 = it },
                                    label = { Text("Address Line 1") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = city,
                                    onValueChange = { city = it },
                                    label = { Text("City") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = state,
                                    onValueChange = { state = it.take(2) },
                                    label = { Text("State") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = zipCode,
                                    onValueChange = { zipCode = it.take(5) },
                                    label = { Text("Zip Code") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(16.dp))

                                Row(
                                    horizontalArrangement = Arrangement.End,
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    if (isEditing) {
                                        TextButton(
                                            onClick = {
                                                displayName = profile.displayName ?: ""
                                                organizationName = currentOrganization.organizationName.orEmpty()
                                                organizationPhone = currentOrganization.phone.orEmpty()
                                                contactPerson = currentOrganization.contactPerson.orEmpty()
                                                addressLine1 = currentOrganization.address?.line1.orEmpty()
                                                city = currentOrganization.address?.city.orEmpty()
                                                state = currentOrganization.address?.state.orEmpty()
                                                zipCode = currentOrganization.address?.zipCode.orEmpty()

                                                isEditing = false
                                                displayNameError = false
                                            }
                                        ) {
                                            Text("Cancel")
                                        }

                                        Spacer(Modifier.width(12.dp))

                                        Button(
                                            onClick = {
                                                if (displayName.isBlank()) {
                                                    displayNameError = true
                                                    return@Button
                                                }

                                                val updatedOrg = uiState.organization.copy(
                                                    organizationName = organizationName,
                                                    phone = organizationPhone,
                                                    contactPerson = contactPerson,
                                                    address = Address(
                                                        line1 = addressLine1,
                                                        city = city,
                                                        state = state,
                                                        zipCode = zipCode
                                                    )
                                                )

                                                val updatedProfile =
                                                    profile.copy(displayName = displayName)

                                                coroutineScope.launch {
                                                    onUpdateOrganization(updatedOrg)
                                                    appState.authViewModel.updateProfile(updatedProfile)
                                                    appState.authViewModel.setProfile(updatedProfile)
                                                }

                                                isEditing = false
                                                displayNameError = false
                                            },
                                            shape = RoundedCornerShape(12.dp)
                                        ) {
                                            Text("Save")
                                        }
                                    } else {
                                        Button(
                                            onClick = { isEditing = true },
                                            shape = RoundedCornerShape(12.dp)
                                        ) {
                                            Text("Edit")
                                        }
                                    }
                                }
                            }

                            Spacer(Modifier.height(16.dp))

                            AccountCard(
                                title = "Stripe",
                                expanded = expandStripe,
                                onToggle = { expandStripe = !expandStripe },
                                interactionSource = stripeInteraction,
                                shape = cardShape
                            ) {
                                val stripeUiState by appState.stripeViewModel.uiState.collectAsState()
                                val stripeAccount = stripeUiState.toStripeAccount()

                                when {
                                    stripeAccount?.onboardingComplete == true -> {
                                        Text(
                                            text = "Your Stripe account is connected!",
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = Color(0xFF2E7D32)
                                        )
                                        Spacer(Modifier.height(16.dp))
                                        Row(
                                            horizontalArrangement = Arrangement.End,
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Button(
                                                onClick = {
                                                    isConnectingToStripe = true
                                                    coroutineScope.launch {
                                                        try { stripeUiState.dashboardUrl?.let(onDashboardLinkClick) }
                                                        finally { kotlinx.coroutines.delay(5000); isConnectingToStripe = false }
                                                    }
                                                },
                                                shape = RoundedCornerShape(12.dp),
                                                colors = ButtonDefaults.buttonColors(
                                                    containerColor = MaterialTheme.colorScheme.primary,
                                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                                )
                                            ) {
                                                Text("Stripe Dashboard")
                                            }
                                        }
                                    }

                                    stripeAccount?.currentlyDue?.isNotEmpty() == true -> {
                                        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth()) {
                                            Text(
                                                text = "Stripe onboarding isn’t done yet! \uD83D\uDEA7",
                                                style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold),
                                                color = Color(0xFFD32F2F),
                                                textAlign = TextAlign.Center
                                            )
                                            Spacer(Modifier.height(12.dp))
                                            Text(
                                                "Stripe still needs the following:",
                                                style = MaterialTheme.typography.bodyMedium,
                                                textAlign = TextAlign.Center
                                            )
                                            Spacer(Modifier.height(8.dp))
                                            stripeAccount.currentlyDue.forEach { item ->
                                                val friendlyName = when (item) {
                                                    "individual.verification.document" -> "Verification Document"
                                                    "company.verification.document" -> "Business Verification Document"
                                                    "external_account" -> "Bank Account"
                                                    "business_profile.url" -> "Business Website"
                                                    "business_profile.mcc" -> "Business Category (MCC)"
                                                    "business_profile.support_phone" -> "Support Phone Number"
                                                    "company.name" -> "Company Name"
                                                    "company.phone" -> "Company Phone"
                                                    "company.tax_id" -> "Tax ID / EIN"
                                                    "company.address.line1" -> "Business Address Line 1"
                                                    "company.address.city" -> "City"
                                                    "company.address.state" -> "State"
                                                    "company.address.postal_code" -> "ZIP / Postal Code"
                                                    "representative.first_name" -> "Representative First Name"
                                                    "representative.last_name" -> "Representative Last Name"
                                                    "representative.email" -> "Representative Email"
                                                    "representative.phone" -> "Representative Phone"
                                                    "representative.dob.day", "representative.dob.month", "representative.dob.year" -> "Representative Date of Birth"
                                                    "representative.address.line1" -> "Representative Address Line 1"
                                                    "representative.address.city" -> "Representative City"
                                                    "representative.address.state" -> "Representative State"
                                                    "representative.address.postal_code" -> "Representative ZIP"
                                                    "representative.relationship.executive" -> "Representative Role / Executive"
                                                    "representative.relationship.title" -> "Representative Title"
                                                    "representative.ssn_last_4" -> "Representative SSN Last 4 Digits"
                                                    "tos_acceptance.date", "tos_acceptance.ip" -> "Terms of Service Acceptance"
                                                    "settings.payments.statement_descriptor" -> "Statement Descriptor"
                                                    else -> item.replace("_", " ").replace(".", " ").capitalize()
                                                }
                                                Text(text = "• $friendlyName", style = MaterialTheme.typography.bodyMedium)
                                            }
                                            Spacer(Modifier.height(20.dp))
                                            Surface(
                                                color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.6f),
                                                shape = RoundedCornerShape(16.dp),
                                                modifier = Modifier.fillMaxWidth()
                                            ) {
                                                Text(
                                                    text = "You’re almost done! Open your Stripe Dashboard → Menu → Tasks page to finish setup.",
                                                    style = MaterialTheme.typography.bodySmall.copy(
                                                        fontWeight = FontWeight.Medium,
                                                        lineHeight = 18.sp
                                                    ),
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                                    textAlign = TextAlign.Center,
                                                    modifier = Modifier.padding(12.dp)
                                                )
                                            }
                                            Spacer(Modifier.height(16.dp))
                                            Row(
                                                horizontalArrangement = Arrangement.End,
                                                modifier = Modifier.fillMaxWidth()
                                            ) {
                                                Button(
                                                    onClick = {
                                                        isConnectingToStripe = true
                                                        coroutineScope.launch {
                                                            try { stripeUiState.dashboardUrl?.let(onDashboardLinkClick) }
                                                            finally { kotlinx.coroutines.delay(5000); isConnectingToStripe = false }
                                                        }
                                                    },
                                                    shape = RoundedCornerShape(12.dp),
                                                    colors = ButtonDefaults.buttonColors(
                                                        containerColor = MaterialTheme.colorScheme.primary,
                                                        contentColor = MaterialTheme.colorScheme.onPrimary
                                                    )
                                                ) {
                                                    Text("Stripe Dashboard")
                                                }
                                            }
                                        }
                                    }

                                    stripeAccount != null || uiState.isStripeSetupStarted -> {
                                        Text(
                                            "Connect or create a Stripe account to enable online ticket sales for your events.",
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                        Spacer(Modifier.height(12.dp))
                                        HorizontalDivider()
                                        Spacer(Modifier.height(12.dp))
                                        Text(
                                            "Complete your Stripe setup.",
                                            style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold),
                                            color = Color(0xFFD32F2F),
                                            textAlign = TextAlign.Center,
                                            modifier = Modifier.fillMaxWidth()
                                        )
                                        Spacer(Modifier.height(16.dp))
                                        Row(
                                            horizontalArrangement = Arrangement.End,
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Button(
                                                onClick = {
                                                    isConnectingToStripe = true
                                                    coroutineScope.launch {
                                                        try { stripeAccount?.accountId?.let(onResumeStripeSetup) }
                                                        finally { kotlinx.coroutines.delay(5000); isConnectingToStripe = false }
                                                    }
                                                },
                                                shape = RoundedCornerShape(12.dp),
                                                colors = ButtonDefaults.buttonColors(
                                                    containerColor = MaterialTheme.colorScheme.primary,
                                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                                )
                                            ) {
                                                Text("Resume Setup")
                                            }
                                        }
                                    }

                                    else -> {
                                        Text(
                                            text = "Connect or create a Stripe account to enable online ticket sales for your events.",
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                        Spacer(Modifier.height(12.dp))
                                        HorizontalDivider()
                                        Spacer(Modifier.height(12.dp))
                                        Row(
                                            horizontalArrangement = Arrangement.End,
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Button(
                                                onClick = {
                                                    isConnectingToStripe = true
                                                    coroutineScope.launch {
                                                        try {
                                                            onStripeSetupClick(profile.email)
                                                            appState.authViewModel.markStripeSetupStarted()
                                                            appState.authViewModel.updateStripeAccountPlaceholder()
                                                            appState.authViewModel.loadOrganizationData(profile._id)
                                                        } finally { kotlinx.coroutines.delay(5000); isConnectingToStripe = false }
                                                    }
                                                },
                                                shape = RoundedCornerShape(12.dp),
                                                colors = ButtonDefaults.buttonColors(
                                                    containerColor = MaterialTheme.colorScheme.primary,
                                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                                )
                                            ) {
                                                Text("Setup")
                                            }
                                        }
                                    }
                                }
                            }

                            Spacer(Modifier.height(16.dp))

                            AccountCard(
                                title = "Account",
                                expanded = expandSettings,
                                onToggle = { expandSettings = !expandSettings },
                                interactionSource = settingsInteraction,
                                shape = cardShape
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Button(
                                        onClick = {
                                            onToggleAccount()
                                        },
                                        shape = RoundedCornerShape(12.dp),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = if (isDeactivated)
                                                MaterialTheme.colorScheme.primary
                                            else
                                                MaterialTheme.colorScheme.error
                                        ),
                                        modifier = Modifier.width(120.dp)
                                    ) {
                                        Text(if (isDeactivated) "Reactivate" else "Deactivate")
                                    }

                                    Spacer(Modifier.width(16.dp))

                                    Text(
                                        text = if (isDeactivated) "If you'd like to keep your account." else "This must be done before an account can be deleted and it will pause all event activity.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        modifier = Modifier
                                            .weight(1f)
                                            .wrapContentWidth(Alignment.CenterHorizontally),
                                        textAlign = TextAlign.Center
                                    )
                                }

                                Spacer(Modifier.height(16.dp))

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Button(
                                        onClick = { showDeleteDialog = true },
                                        enabled = isDeactivated,
                                        shape = RoundedCornerShape(12.dp),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = if (isDeactivated)
                                                MaterialTheme.colorScheme.error
                                            else
                                                MaterialTheme.colorScheme.surfaceVariant
                                        ),
                                        modifier = Modifier.width(120.dp)
                                    ) {
                                        Text("Delete")
                                    }

                                    Spacer(Modifier.width(16.dp))

                                    Text(
                                        text = "Permanently remove account.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        modifier = Modifier
                                            .weight(1f)
                                            .wrapContentWidth(Alignment.CenterHorizontally),
                                        textAlign = TextAlign.Center
                                    )
                                }

                                if (showDeleteDialog) {
                                    ConfirmDeleteDialog(
                                        title = "Delete Account?",
                                        message = "This action is permanent and cannot be undone. All data associated with your account will be permanently removed.",
                                        onConfirm = {
                                            showDeleteDialog = false
                                            onConfirmDeleteAccount()
                                            onSignOut()
                                        },
                                        onDismiss = { showDeleteDialog = false }
                                    )
                                }
                            }

                            Spacer(Modifier.height(24.dp))

                            Button(
                                onClick = onSignOut,
                                shape = RoundedCornerShape(12.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.primary,
                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                ),
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            ) {
                                Text("Sign Out")
                            }
                        }
                    }
                }
            }
        }

        if (isConnectingToStripe) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.45f)),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    CircularProgressIndicator(
                        color = MaterialTheme.colorScheme.primary,
                        strokeWidth = 4.dp,
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(12.dp))
                    Text(
                        text = "Connecting to Stripe...",
                        style = MaterialTheme.typography.bodyMedium.copy(color = Color.White)
                    )
                }
            }
        }
    }
}